import { useEffect, useState } from "react";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import { useMutation, useQuery } from "@tanstack/react-query";
import Input from "../../../form/Input";
import { toast } from "react-toastify";
import DatePicker from "../../../form/DatePicker";
import { useNavigate, useParams } from "react-router-dom";
import FlexibleSelect from "../../../form/select/FlexibleSelect";
import { userList } from "../../../../services/userService";
import { Check } from "lucide-react";
import { quizAddEdit, quizDetails } from "../../../../services/quizService";

const validation = {
  title: { required: true, message: "Question is required" },
};

const ManageQuiz = ({ getResponse = () => {}, close = () => {} }) => {
  const params = useParams();
  const navigate = useNavigate();
  const id = params?.id;
  const [coverMedia, setCoverMedia] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const { values, handleChange, handleSubmit, errors, setFieldsValue, formValidate } = useForm({
    validationRules: validation,
    initialState: { status: "active" },
  });
  const [artist, setArtist] = useState("");
  const [options, setOptions] = useState([
    { id: crypto.randomUUID(), label: { text: "" }, isCorrect: false },
    { id: crypto.randomUUID(), label: { text: "" }, isCorrect: false },
    { id: crypto.randomUUID(), label: { text: "" }, isCorrect: false },
    { id: crypto.randomUUID(), label: { text: "" }, isCorrect: false },
  ]);

  const { data: details } = useQuery({
    queryKey: ["quiz-details", id],
    queryFn: async () => {
      const res = await quizDetails({ id: id });
      return res?.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (details?._id) {
      setFieldsValue({
        ...details,
        title: details?.label?.text,
        publishStartDate: details?.schedule?.startDate,
        publishEndDate: details?.schedule?.endDate,
      });
      setArtist(details?.user);
      setCoverMedia(details?.coverMedia);

      if (details?.options?.length) {
        const normalizedOptions = details.options.map((opt) => ({
          id: opt?.id || crypto.randomUUID(),
          label: { text: opt?.label?.text || "" },
          isCorrect: !!opt?.isCorrect,
        }));
        setOptions(normalizedOptions);
      }
    }
  }, [details]);

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      return await quizAddEdit({
        label: {
          text: val?.title,
          // image: coverMedia?._id,
        },
        sectionType: "quiz",
        status: val?.status,
        type: "radio",
        user: artist,
        publishStartDate: val?.publishStartDate,
        publishEndDate: val?.publishEndDate,
        options: options,
        points: Number(val?.points) || 0,
        timeLimit: Number(val?.timeLimit) || 0,
        ...(details?._id && { id: details._id }),
      });
    },

    onSuccess: (res) => {
      getResponse(res);
      if (res?.status === 200 || res?.status === 201) {
        navigate(`/admin/quiz`);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { data: userData } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const res = await userList({
        limit: 10000,
        role: "artist",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const handleOptionChange = (id, value) => {
    setOptions((prev) => prev.map((opt) => (opt.id === id ? { ...opt, label: { text: value } } : opt)));
  };

  const handleCorrectChange = (id) => {
    setOptions((prev) => prev.map((opt) => ({ ...opt, isCorrect: opt.id === id })));
  };

  const addOption = () => {
    setOptions((prev) => [...prev, { id: crypto.randomUUID(), label: { text: "" }, isCorrect: false }]);
  };

  const removeOption = (id) => {
    setOptions((prev) => prev.filter((opt) => opt.id !== id));
  };

  return (
    <>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);

          const areOptionsFilled = options.every((opt) => opt.label.text.trim());
          const hasCorrectAnswer = options.some((opt) => opt.isCorrect);

          if (!areOptionsFilled || !hasCorrectAnswer) {
            return;
          }

          handleSubmit(event, (formValues) => {
            mutate({ ...formValues, options });
          });
        }}
      >
        <div className="flex justify-between items-center w-full px-6">
          <div className="font-normal text-lg xl:text-3xl text-primary">Manage Quizes</div>
          <div className="flex gap-4">
            <Button
              buttonType="submit"
              buttonLabel="Save Quiz"
              buttonClasses="min-w-28"
              buttonLabelClasses="mx-auto"
              {...(isLoading && {
                loading: true,
                buttonIconPosition: "left",
                isDisable: true,
              })}
            />
            <Button
              buttonLabel="Cancel"
              buttonClasses="min-w-28 !bg-white !text-primary border !border-primary"
              buttonLabelClasses="mx-auto"
              buttonHasLink={true}
              buttonLink={"/admin/quiz"}
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-6 p-6">
          <div className="col-span-3 space-y-4">
            <Input
              label="Question"
              inputPlaceholder="Enter question"
              onChange={(e) => handleChange({ target: { name: "title", value: e.target.value } })}
              inputName={"title"}
              required={false}
              value={values?.title || ""}
              {...formValidate(errors, "title")}
            />

            <div className="text-xs font-medium text-text-disabled ">
              Quiz Options
              <span className="text-[8px] text-red-500 ml-1 relative top-[-2px]">
                <i className="fa-solid fa-asterisk"></i>
              </span>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
              <div className="grid grid-cols-3 gap-4 px-2 text-xs font-semibold text-gray-600 border-b pb-2">
                <div>Correct Answer</div>
                <div>Options</div>
                <div></div>
              </div>

              {options.map((option, index) => (
                <div
                  key={option.id}
                  className="grid grid-cols-3 gap-4 items-center p-4 border rounded-lg shadow-sm bg-gray-50 hover:shadow-md transition"
                >
                  <label className="flex items-center justify-start cursor-pointer">
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={option.isCorrect}
                      onChange={() => handleCorrectChange(option.id)}
                      className="hidden peer"
                    />
                    <span className="w-6 h-6 flex items-center justify-center border-2 border-primary rounded-md peer-checked:bg-primary">
                      <Check className="w-4 h-4 text-white" />
                    </span>
                  </label>

                  <input
                    type="text"
                    placeholder={`Option ${index + 1}`}
                    value={option?.label?.text}
                    onChange={(e) => handleOptionChange(option.id, e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-pink-400 ${
                      submitted && !option?.label?.text?.trim() ? "border-red-500" : "border-gray-300"
                    }`}
                  />

                  {options.length > 2 && (
                    <button type="button" onClick={() => removeOption(option.id)} className="text-red-500 hover:text-red-700 text-sm font-bold">
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                buttonLabel="+ Add Options"
                buttonClasses="mt-3 w-[140px] !h-[40px] 
    bg-transparent 
    font-semibold rounded-lg 
    text-transparent bg-clip-text 
    bg-gradient-to-r from-fuchsia-500 to-purple-600 
    hover:opacity-90"
                buttonFunction={addOption}
              />
            </div>
            {submitted && !options.every((opt) => opt.label.text.trim()) && (
              <div className="text-xs text-amber-600 mt-1">
                <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
                All options are required
              </div>
            )}

            {submitted && options.every((opt) => opt.label.text.trim()) && !options.some((opt) => opt.isCorrect) && (
              <div className="text-xs text-amber-600 mt-1">
                <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
                Please select a correct answer
              </div>
            )}

            <div className="relative col-span-4 mt-5">
              <div className="flex gap-4">
                <Button
                  buttonType="submit"
                  buttonLabel={"Save Quiz"}
                  buttonClasses={"min-w-28"}
                  buttonLabelClasses={"mx-auto"}
                  {...(isLoading && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
                <Button
                  buttonLabel={"Cancel"}
                  buttonClasses={"min-w-28 !bg-white !text-primary border !border-primary"}
                  buttonLabelClasses={"mx-auto"}
                  buttonHasLink={true}
                  buttonLink={"/admin/quiz"}
                />
              </div>
            </div>
          </div>

          <div className="col-span-1 space-y-6 mt-5">
            {/* <div className="border border-gray-300 rounded-lg p-4 space-y-2">
              <SelectSingleImage
                label="Upload Cover Media"
                size="lg"
                value={coverMedia}
                onChange={(img) => setCoverMedia(img)}
                onRemove={() => setCoverMedia(null)}
              />
            </div> */}

            <div className="border border-gray-300 rounded-lg p-4 space-y-2">
              <DatePicker
                label={"Publish Start Date"}
                placeholder={"Start date"}
                position="fixed"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "publishStartDate",
                      value: e?.startDate || e?.endDate,
                    },
                  })
                }
                value={{
                  startDate: values?.publishStartDate,
                  endDate: values?.publishStartDate,
                }}
              />
              <DatePicker
                label={"Publish End Date"}
                placeholder={"End date"}
                position="fixed"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "publishEndDate",
                      value: e?.startDate || e?.endDate,
                    },
                  })
                }
                value={{
                  startDate: values?.publishEndDate,
                  endDate: values?.publishEndDate,
                }}
              />
              <FlexibleSelect
                label={"Status"}
                placeholder={"Choose status"}
                onChange={(e) => handleChange({ target: { name: "status", value: e } })}
                selectedValue={values?.status}
                dropdownData={[
                  { name: "Active", value: "active" },
                  { name: "Inactive", value: "inactive" },
                ]}
                {...formValidate(errors, "status")}
              />
              <FlexibleSelect
                label={"Select Artist"}
                isSearch={true}
                dropdownData={userData?.data?.map((item) => {
                  return {
                    name: `${item?.firstName} ${item?.lastName}`,
                    value: item?._id,
                    image: item?.image?.url,
                  };
                })}
                selectedValue={artist}
                onChange={setArtist}
                type="single"
                placeholder={"Pick Artist"}
              />
              <Input label={"Reward Points"} inputPlaceholder="Add Points" inputName={"points"} value={values?.points} onChange={handleChange} />
              <Input label={"Quiz Timer (sec)"} inputPlaceholder="Add time limit" inputName={"timeLimit"} value={values?.timeLimit} onChange={handleChange} />
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default ManageQuiz;